// Screenplay PDF export utility — renders Hollywood-standard formatted pages
// using jsPDF for precise control over margins, indents, and pagination.
//
// Industry standard measurements:
//   US Letter 8.5" x 11", Courier 12pt
//   Margins: 1.5" left, 1" right, 1" top, 1" bottom
//   Character indent: 3.7" from left edge (2.2" from left margin)
//   Parenthetical indent: 3.1" from left edge (1.6" from left margin)
//   Dialogue indent: 2.5" from left edge (1.0" from left margin)
//   Dialogue width: 3.5"
//   Page number: 0.5" from top, right-aligned at right margin, format "N."

import jsPDF from 'jspdf';
import {
  SCENE_PREFIXES,
  TRANSITIONS,
  CHARACTER_EXTENSIONS,
  normalizeNewlines,
  standardizeExtension,
} from './fountainParser';

// Page measurements in inches
const MEASUREMENTS = {
  PAGE_WIDTH: 8.5,
  PAGE_HEIGHT: 11,
  LEFT_MARGIN: 1.5,
  RIGHT_MARGIN: 1.0,
  TOP_MARGIN: 1.0,
  BOTTOM_MARGIN: 1.0,
  CHARACTER_INDENT: 2.2,
  PARENTHETICAL_INDENT: 1.6,
  DIALOGUE_INDENT: 1.0,
  DIALOGUE_WIDTH: 3.5,
  PAGE_NUMBER_TOP: 0.5,
};

// Font settings
const FONT_SIZE = 12; // 12pt
const LINE_HEIGHT = 12 * 1.19; // ~14.28pt per line (matching editor)
const CHARS_PER_INCH = 10; // Courier 12pt = 10 chars/inch

// Convert inches to points (1 inch = 72 points)
const in2pt = (inches) => inches * 72;

// Wrap text to a given width in inches
const wrapText = (text, widthInches) => {
  const maxChars = Math.floor(widthInches * CHARS_PER_INCH);
  if (!text || text.length === 0) return [''];
  const words = text.split(' ');
  const lines = [];
  let current = '';
  for (const word of words) {
    if (current.length === 0) {
      current = word;
    } else if ((current + ' ' + word).length <= maxChars) {
      current += ' ' + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
};

// Parse Fountain content into structured elements
const parseFountain = (content) => {
  const scriptContent = normalizeNewlines(String(content))
    .replace(/\/\*[\s\S]*?\*\//g, '') // strip boneyard
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const lines = scriptContent.split('\n');
  const elements = [];
  let previousElement = null;
  let inDualDialogue = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const upper = trimmed.toUpperCase();
    let elementType;
    let isForced = false;
    let extension = '';
    let isDual = false;
    let sceneNumber = '';

    if (!trimmed) {
      elements.push({ key: i, type: 'blank', text: '' });
      continue;
    }

    // Forced page break
    if (/^={3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
      elements.push({ key: i, type: 'pageBreak', text: '' });
      continue;
    }

    // Sections and synopses — skip in PDF
    if (/^#{1,6}\s/.test(trimmed) || /^=(?!=)/.test(trimmed)) {
      continue;
    }

    // Dual dialogue marker
    if (trimmed.endsWith('^')) {
      isDual = true;
      inDualDialogue = true;
    }

    // Context checks
    const prevLine = i > 0 ? lines[i - 1].trim() : '';
    const nextLine = i < lines.length - 1 ? lines[i + 1].trim() : '';
    const hasBlankBefore = i === 0 || prevLine === '';
    const hasDialogueAfter = nextLine !== '' &&
      !/^(INT\.|EXT\.|INT\/EXT\.|INT\.\/EXT\.|I\/E\.|EST\.)/i.test(nextLine) &&
      !TRANSITIONS.some(t => nextLine.toUpperCase().startsWith(t));

    // Fountain syntax markers
    if (trimmed.startsWith('!')) {
      isForced = true; elementType = 'action';
    } else if (trimmed.startsWith('.') && /^\.[a-zA-Z0-9]/.test(trimmed)) {
      isForced = true; elementType = 'scene';
    } else if (trimmed.startsWith('@')) {
      isForced = true; elementType = 'character';
    } else if (trimmed.startsWith('>') && trimmed.endsWith('<')) {
      isForced = true; elementType = 'centered';
    } else if ((trimmed.startsWith('>') || trimmed.endsWith('<')) && !trimmed.endsWith('>')) {
      isForced = true; elementType = 'transition';
    } else if (trimmed.startsWith('~')) {
      isForced = true; elementType = 'lyric';
    } else if (trimmed.startsWith('[[')) {
      elementType = 'note';
    } else {
      const upperNoEmphasis = upper.replace(/^[_*]+/, '');
      const isSceneHeader = SCENE_PREFIXES.some(prefix =>
        upper.startsWith(prefix) || upper.startsWith('.' + prefix) ||
        upperNoEmphasis.startsWith(prefix) || upperNoEmphasis.startsWith('.' + prefix)
      );

      if (isSceneHeader) {
        elementType = 'scene';
        inDualDialogue = false;
        const sceneNumMatch = trimmed.match(/#([a-zA-Z0-9.\-]+)#\s*$/);
        if (sceneNumMatch) sceneNumber = sceneNumMatch[1];
      } else if (TRANSITIONS.includes(upper) || (upper.endsWith('TO:') && upper === upper.toUpperCase())) {
        elementType = 'transition';
        inDualDialogue = false;
      } else if ((hasBlankBefore || hasDialogueAfter) &&
                 trimmed.replace(/\s*\^$/, '').match(/^[A-Z][A-Z0-9\s\-\']{1,28}(\([^)]*\))?$/) &&
                 !SCENE_PREFIXES.some(prefix => upper.replace(/\s*\^$/, '').toUpperCase().startsWith(prefix)) &&
                 !TRANSITIONS.some(t => upper.replace(/\s*\^$/, '').toUpperCase().startsWith(t)) &&
                 !upper.startsWith('FADE') && !upper.startsWith('CUT') &&
                 !upper.replace(/\s*\^$/, '').toUpperCase().endsWith('TO')) {
        const match = trimmed.match(/^([A-Z][A-Z0-9\s\-\']*?)(\([^)]*\))?\s*\^?$/);
        if (match) {
          elementType = 'character';
          if (match[2]) extension = match[2].trim();
        }
      } else if (trimmed.match(/^\([^)]*\)$/)) {
        elementType = 'parenthetical';
      } else if (previousElement === 'character' || previousElement === 'parenthetical' || inDualDialogue) {
        elementType = 'dialogue';
      } else {
        elementType = 'action';
        inDualDialogue = false;
      }
    }

    // Clean text
    let cleaned = trimmed
      .replace(/^[!@>~]/, '')
      .replace(/^\.(?=[a-zA-Z0-9])/, '')
      .replace(/\[\[[^\]]*\]\]/g, '')
      .replace(/^>|<$/g, '')
      .replace(/\s*\^$/, '')
      .replace(/#([a-zA-Z0-9.\-]+)#\s*$/, '')
      .trim();

    if (elementType === 'character') {
      let characterName = cleaned;
      if (extension) {
        extension = standardizeExtension(extension);
        characterName = characterName.replace(/\([^)]*\)/, '').trim();
      }
      cleaned = characterName.toUpperCase() + (extension ? ' ' + extension : '');
    }
    if (elementType === 'scene') cleaned = cleaned.toUpperCase();
    if (elementType === 'transition') {
      cleaned = cleaned.toUpperCase();
      const isSingleWord = !cleaned.includes(' ');
      const isKnown = TRANSITIONS.includes(cleaned) || cleaned.endsWith('TO:') ||
        cleaned.includes('FADE') || cleaned.includes('CUT') || cleaned.includes('DISSOLVE') || cleaned.includes('BLACK');
      if (isSingleWord && !isKnown) cleaned += ' TO:';
    }

    elements.push({
      key: i, type: elementType, text: cleaned, isForced, isDual,
      extension: elementType === 'character' ? extension : undefined,
      sceneNumber: elementType === 'scene' ? sceneNumber : undefined,
    });
    previousElement = elementType;
  }

  // Apply standard spacing
  const nonBlank = elements.filter(el => el.type !== 'blank' && el.type !== 'note');
  const spaced = [];
  let blankCounter = 0;
  const addBlank = () => spaced.push({ key: `sp-${blankCounter++}`, type: 'blank', text: '' });

  for (let idx = 0; idx < nonBlank.length; idx++) {
    const el = nonBlank[idx];
    const prev = idx > 0 ? nonBlank[idx - 1] : null;
    if (idx === 0) { spaced.push(el); continue; }
    if (el.type === 'pageBreak') { spaced.push(el); continue; }
    const dialogueTypes = ['dialogue', 'lyric'];
    const isDialogueContinuation =
      !el.isDual && (
        (dialogueTypes.includes(el.type) && (prev.type === 'character' || prev.type === 'parenthetical')) ||
        (el.type === 'parenthetical' && (prev.type === 'character' || dialogueTypes.includes(prev.type))) ||
        (el.type === 'lyric' && dialogueTypes.includes(prev.type))
      );
    if (isDialogueContinuation) {
      spaced.push(el);
    } else {
      addBlank();
      spaced.push(el);
    }
  }
  return spaced;
};

// Get wrapped lines for an element
const getElementLines = (el) => {
  const contentWidth = MEASUREMENTS.PAGE_WIDTH - MEASUREMENTS.LEFT_MARGIN - MEASUREMENTS.RIGHT_MARGIN;
  const dialogueWidth = MEASUREMENTS.DIALOGUE_WIDTH;
  const parentheticalWidth = contentWidth - (MEASUREMENTS.PARENTHETICAL_INDENT * 2);

  switch (el.type) {
    case 'scene':
    case 'action':
    case 'transition':
    case 'centered':
      return wrapText(el.text || '', contentWidth);
    case 'character':
      return wrapText(el.text || '', contentWidth - MEASUREMENTS.CHARACTER_INDENT);
    case 'parenthetical':
      return wrapText(el.text || '', parentheticalWidth);
    case 'dialogue':
    case 'lyric':
      return wrapText(el.text || '', dialogueWidth);
    case 'blank':
      return [''];
    default:
      return [''];
  }
};

// Get X position (from left edge of page) for an element
const getElementX = (el) => {
  switch (el.type) {
    case 'character':
      return MEASUREMENTS.LEFT_MARGIN + MEASUREMENTS.CHARACTER_INDENT;
    case 'parenthetical':
      return MEASUREMENTS.LEFT_MARGIN + MEASUREMENTS.PARENTHETICAL_INDENT;
    case 'dialogue':
    case 'lyric':
      return MEASUREMENTS.LEFT_MARGIN + MEASUREMENTS.DIALOGUE_INDENT;
    case 'transition':
      // Right-aligned — handled specially in render
      return MEASUREMENTS.LEFT_MARGIN;
    case 'centered':
      // Center-aligned — handled specially in render
      return MEASUREMENTS.LEFT_MARGIN;
    default:
      return MEASUREMENTS.LEFT_MARGIN;
  }
};

// Check if element is part of a dialogue block
const isDialogueElement = (t) => ['character', 'parenthetical', 'dialogue', 'lyric'].includes(t);

/**
 * Export a screenplay to PDF with Hollywood-standard formatting.
 *
 * @param {Object} options
 * @param {string} options.content - Fountain-format screenplay text
 * @param {string} options.title - Script title (for filename)
 * @param {Object} options.titlePage - Optional title page data { title, credit, author, source, draft, contact }
 */
export function exportScreenplayToPdf({ content, title, titlePage }) {
  const pdf = new jsPDF({
    unit: 'in',
    format: 'letter',
    orientation: 'portrait',
  });

  pdf.setFont('Courier', 'normal');
  pdf.setFontSize(FONT_SIZE);

  const pageWidth = MEASUREMENTS.PAGE_WIDTH;
  const pageHeight = MEASUREMENTS.PAGE_HEIGHT;
  const contentTop = MEASUREMENTS.TOP_MARGIN;
  const contentBottom = pageHeight - MEASUREMENTS.BOTTOM_MARGIN;
  const contentHeight = contentBottom - contentTop;
  const linesPerPage = Math.floor(contentHeight / (LINE_HEIGHT / 72));

  let currentPage = 1;
  let currentY = contentTop;
  let currentScene = null;

  // ─── Title Page ───────────────────────────────────────────────
  if (titlePage) {
    const { title: tpTitle, credit, author, source, draft, copyright, contact } = titlePage;
    // Title at ~1/3 down the page (3.5" from top)
    let titleY = 3.5;
    if (tpTitle) {
      pdf.setFont('Courier', 'bold');
      const titleUpper = tpTitle.toUpperCase();
      const titleWidth = pdf.getTextWidth(titleUpper) / 72;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(titleUpper, titleX, titleY);
      pdf.setFont('Courier', 'normal');
      titleY += LINE_HEIGHT / 72;
    }
    if (credit) {
      const creditWidth = pdf.getTextWidth(credit) / 72;
      pdf.text(credit, (pageWidth - creditWidth) / 2, titleY);
      titleY += (LINE_HEIGHT / 72) * 3; // 2 blank lines
    }
    if (author) {
      if (!credit) {
        pdf.text('by', (pageWidth - pdf.getTextWidth('by') / 72) / 2, titleY);
        titleY += LINE_HEIGHT / 72;
      }
      const authorWidth = pdf.getTextWidth(author) / 72;
      pdf.text(author, (pageWidth - authorWidth) / 2, titleY);
      titleY += LINE_HEIGHT / 72;
    }
    if (source) {
      titleY += LINE_HEIGHT / 72;
      const sourceWidth = pdf.getTextWidth(source) / 72;
      pdf.text(source, (pageWidth - sourceWidth) / 2, titleY);
    }
    // Draft date / contact at bottom-left
    let bottomY = contentBottom;
    if (contact) {
      pdf.text(contact, MEASUREMENTS.LEFT_MARGIN, bottomY);
      bottomY -= LINE_HEIGHT / 72;
    }
    if (draft) {
      pdf.text(draft, MEASUREMENTS.LEFT_MARGIN, bottomY);
    }
    pdf.addPage();
  }

  // ─── Helper: add page number ──────────────────────────────────
  const addPageNumber = (pageNum) => {
    const numText = `${pageNum}.`;
    const numWidth = pdf.getTextWidth(numText) / 72;
    pdf.text(numText, pageWidth - MEASUREMENTS.RIGHT_MARGIN - numWidth, MEASUREMENTS.PAGE_NUMBER_TOP);
  };

  // ─── Helper: check if we need a new page ──────────────────────
  const needsNewPage = (linesNeeded = 1) => {
    const remaining = (contentBottom - currentY) / (LINE_HEIGHT / 72);
    return remaining < linesNeeded;
  };

  // ─── Helper: start new page ───────────────────────────────────
  const startNewPage = () => {
    pdf.addPage();
    currentPage++;
    addPageNumber(currentPage);
    currentY = contentTop;
    // Add CONTINUED: at top if we're in the middle of a scene
    if (currentScene) {
      pdf.text('CONTINUED:', MEASUREMENTS.LEFT_MARGIN, currentY);
      currentY += (LINE_HEIGHT / 72) * 2;
    }
  };

  // ─── Helper: render an element ────────────────────────────────
  const renderElement = (el) => {
    const wrappedLines = getElementLines(el);
    const x = getElementX(el);
    const lineH = LINE_HEIGHT / 72;

    for (const textLine of wrappedLines) {
      if (needsNewPage(1)) {
        // Add CONTINUED: at bottom of current page
        if (currentScene) {
          pdf.text('CONTINUED:', MEASUREMENTS.LEFT_MARGIN, contentBottom);
        }
        startNewPage();
      }
      if (el.type === 'transition') {
        // Right-align transitions
        const tw = pdf.getTextWidth(textLine) / 72;
        pdf.text(textLine, pageWidth - MEASUREMENTS.RIGHT_MARGIN - tw, currentY);
      } else if (el.type === 'centered') {
        // Center-align centered text
        const cw = pdf.getTextWidth(textLine) / 72;
        pdf.text(textLine, (pageWidth - cw) / 2, currentY);
      } else {
        pdf.text(textLine, x, currentY);
      }
      currentY += lineH;
    }
  };

  // ─── Helper: render scene heading with scene numbers ──────────
  const renderSceneHeading = (el) => {
    const lineH = LINE_HEIGHT / 72;
    if (needsNewPage(3)) {
      if (currentScene) {
        pdf.text('CONTINUED:', MEASUREMENTS.LEFT_MARGIN, contentBottom);
      }
      startNewPage();
    }
    currentScene = el.text;
    const x = MEASUREMENTS.LEFT_MARGIN;
    if (el.sceneNumber) {
      // Left scene number
      pdf.text(el.sceneNumber, MEASUREMENTS.LEFT_MARGIN - 0.5, currentY);
    }
    pdf.text(el.text, x, currentY);
    if (el.sceneNumber) {
      // Right scene number
      const snWidth = pdf.getTextWidth(el.sceneNumber) / 72;
      pdf.text(el.sceneNumber, pageWidth - MEASUREMENTS.RIGHT_MARGIN + 0.5 - snWidth, currentY);
    }
    currentY += lineH;
  };

  // ─── Parse and render ─────────────────────────────────────────
  const elements = parseFountain(content);

  // Add page number to first page
  addPageNumber(currentPage);

  let dialogueBlock = null;

  const flushDialogueBlock = () => {
    if (!dialogueBlock) return;
    const blockLines = dialogueBlock.reduce((sum, el) => sum + getElementLines(el).length, 0);
    // If block doesn't fit, try to split with (MORE)/(CONT'D)
    const remaining = (contentBottom - currentY) / (LINE_HEIGHT / 72);
    if (blockLines <= remaining || currentY === contentTop) {
      // Fits — render whole block
      for (const el of dialogueBlock) renderElement(el);
    } else {
      // Split: render what fits, add (MORE), continue on next page with (CONT'D)
      const charEl = dialogueBlock[0];
      const charLines = getElementLines(charEl);
      let linesRendered = 0;
      // Render character cue
      for (const line of charLines) {
        if (needsNewPage(1)) { startNewPage(); }
        pdf.text(line, getElementX(charEl), currentY);
        currentY += LINE_HEIGHT / 72;
        linesRendered++;
      }
      // Render as many dialogue lines as fit
      let placedAny = false;
      for (let di = 1; di < dialogueBlock.length; di++) {
        const el = dialogueBlock[di];
        const elLines = getElementLines(el);
        // Check if we need (MORE) line — reserve 1 line for it
        for (let li = 0; li < elLines.length; li++) {
          if (needsNewPage(2)) {
            // Add (MORE)
            pdf.text('(MORE)', getElementX(charEl), currentY);
            if (currentScene) {
              pdf.text('CONTINUED:', MEASUREMENTS.LEFT_MARGIN, contentBottom);
            }
            startNewPage();
            // Add CHARACTER (CONT'D)
            const charName = charEl.text.replace(/\s*\([^)]*\)$/, '').trim();
            const ext = charEl.extension || '';
            const contd = ext ? `${charName} ${ext} (CONT'D)` : `${charName} (CONT'D)`;
            pdf.text(contd, getElementX(charEl), currentY);
            currentY += LINE_HEIGHT / 72;
            placedAny = false;
          }
          pdf.text(elLines[li], getElementX(el), currentY);
          currentY += LINE_HEIGHT / 72;
          placedAny = true;
        }
      }
    }
    dialogueBlock = null;
  };

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];

    // Forced page break
    if (el.type === 'pageBreak') {
      flushDialogueBlock();
      if (currentScene) {
        pdf.text('CONTINUED:', MEASUREMENTS.LEFT_MARGIN, contentBottom);
      }
      pdf.addPage();
      currentPage++;
      addPageNumber(currentPage);
      currentY = contentTop;
      continue;
    }

    // Scene heading
    if (el.type === 'scene') {
      flushDialogueBlock();
      renderSceneHeading(el);
      continue;
    }

    // Dialogue block accumulation
    if (el.type === 'character') {
      flushDialogueBlock();
      dialogueBlock = [el];
      continue;
    }
    if (dialogueBlock && isDialogueElement(el.type)) {
      dialogueBlock.push(el);
      continue;
    }

    // Non-dialogue element — flush any pending dialogue block
    if (dialogueBlock) {
      flushDialogueBlock();
    }

    // Render regular element
    renderElement(el);
  }

  // Flush remaining dialogue block
  flushDialogueBlock();

  // Save
  const filename = (title || 'screenplay').replace(/[^a-zA-Z0-9_\-\s]/g, '').trim() || 'screenplay';
  pdf.save(`${filename}.pdf`);
}
